import React from 'react';
import { Helmet } from 'react-helmet';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import {
  LineChart,
  Search,
  Target,
  BarChart3,
  CheckCircle2,
  ArrowUpRight,
  MapPin,
  TrendingUp,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SafeJsonLd from '@/components/SafeJsonLd';
import { useLanguage } from '@/contexts/LanguageContext';
import ServicePricing, { PricingTier } from '@/components/service/ServicePricing';
import ServiceFAQ, { FAQItem } from '@/components/service/ServiceFAQ';
import ServiceCrossSell from '@/components/service/ServiceCrossSell';
import ServiceBreadcrumb from '@/components/service/ServiceBreadcrumb';

const ServicesSeo: React.FC = () => {
  const { t, language } = useLanguage();
  const isPT = language === 'pt';

  const features = [
    { Icon: Search,    key: 'seo' },
    { Icon: Target,    key: 'ads' },
    { Icon: BarChart3, key: 'analytics' },
  ] as const;

  const benefitsRaw = t('servicesSeo.benefits') as unknown;
  const benefits: string[] = Array.isArray(benefitsRaw) ? (benefitsRaw as string[]) : [];

  const title = isPT
    ? 'SEO & Google Ads para Pequenas Empresas | Spawncore'
    : 'SEO & Google Ads for Small Businesses | Spawncore';

  const description = isPT
    ? 'SEO local, Google Ads e Meta Ads para pequenas empresas em Portugal. Foco em retorno mensurável: tráfego qualificado, leads e vendas. Sem promessas vazias.'
    : 'Local SEO, Google Ads and Meta Ads for small businesses in Portugal. Focused on measurable return: qualified traffic, leads and sales. No empty promises.';

  const tiers: PricingTier[] = isPT ? [
    {
      name: 'Local SEO',
      price: '€300',
      period: '/mês',
      description: 'Apareça quando alguém procura na sua zona.',
      features: [
        'Auditoria técnica inicial',
        'Otimização Google Business Profile',
        'SEO on-page (até 10 páginas)',
        'Pesquisa de palavras-chave locais',
        '2 artigos de blog/mês',
        'Reviews monitoring',
        'Relatório mensal de posições',
      ],
      ctaLabel: 'Começar SEO',
    },
    {
      name: 'SEO + Conteúdo',
      price: '€650',
      period: '/mês',
      description: 'Conteúdo que ranqueia e converte.',
      features: [
        'Tudo do Local SEO',
        'Estratégia editorial trimestral',
        '4 artigos otimizados/mês',
        'Link building qualificado',
        'Schema markup avançado',
        'Otimização Core Web Vitals',
        'A/B testing de meta tags',
        'Sessão estratégica mensal',
      ],
      popular: true,
      ctaLabel: 'Escolher Pro',
    },
    {
      name: 'Ads Performance',
      price: '€500',
      period: 'setup + 15% gasto',
      description: 'Google Ads e Meta Ads otimizados ao detalhe.',
      features: [
        'Auditoria de contas atuais',
        'Setup completo Google Ads',
        'Setup completo Meta Ads',
        'Tracking de conversão server-side',
        'Otimização semanal',
        'Landing pages otimizadas',
        'Dashboard Looker em tempo real',
        'Reuniões quinzenais',
      ],
      ctaLabel: 'Falar com o estúdio',
    },
  ] : [
    {
      name: 'Local SEO',
      price: '€300',
      period: '/mo',
      description: 'Show up when someone searches in your area.',
      features: [
        'Initial technical audit',
        'Google Business Profile optimisation',
        'On-page SEO (up to 10 pages)',
        'Local keyword research',
        '2 blog posts/month',
        'Reviews monitoring',
        'Monthly rank report',
      ],
      ctaLabel: 'Start SEO',
    },
    {
      name: 'SEO + Content',
      price: '€650',
      period: '/mo',
      description: 'Content that ranks and converts.',
      features: [
        'Everything in Local SEO',
        'Quarterly editorial strategy',
        '4 optimised articles/month',
        'Qualified link building',
        'Advanced schema markup',
        'Core Web Vitals tuning',
        'Meta tag A/B testing',
        'Monthly strategy session',
      ],
      popular: true,
      ctaLabel: 'Choose Pro',
    },
    {
      name: 'Ads Performance',
      price: '€500',
      period: 'setup + 15% spend',
      description: 'Google Ads and Meta Ads tuned to the detail.',
      features: [
        'Existing accounts audit',
        'Full Google Ads setup',
        'Full Meta Ads setup',
        'Server-side conversion tracking',
        'Weekly optimisation',
        'Optimised landing pages',
        'Real-time Looker dashboard',
        'Bi-weekly meetings',
      ],
      ctaLabel: 'Talk to the studio',
    },
  ];

  const tools = [
    { name: 'Google Search Console', desc: isPT ? 'Monitorização orgânica'   : 'Organic monitoring' },
    { name: 'Google Analytics 4',     desc: isPT ? 'Tráfego e conversões'    : 'Traffic and conversions' },
    { name: 'Ahrefs',                 desc: isPT ? 'Keywords & backlinks'    : 'Keywords & backlinks' },
    { name: 'Semrush',                desc: isPT ? 'Análise competitiva'     : 'Competitive analysis' },
    { name: 'Google Ads',             desc: isPT ? 'Pesquisa & Performance Max' : 'Search & Performance Max' },
    { name: 'Meta Ads',               desc: isPT ? 'FB & IG anúncios'        : 'FB & IG ads' },
    { name: 'Looker Studio',          desc: isPT ? 'Dashboards em tempo real': 'Real-time dashboards' },
    { name: 'PageSpeed Insights',     desc: 'Core Web Vitals' },
  ];

  const useCases = isPT ? [
    { Icon: MapPin,     title: 'Negócios locais',     desc: 'Cafés, restaurantes, clínicas, lojas — domínio do "perto de mim" no Google.' },
    { Icon: TrendingUp, title: 'E-commerce',          desc: 'Páginas de produto otimizadas, Google Shopping, retargeting Meta Ads.' },
    { Icon: Search,     title: 'Serviços B2B',        desc: 'Keywords long-tail, conteúdo técnico, LinkedIn Ads para decisores.' },
    { Icon: BarChart3,  title: 'SaaS / Software',     desc: 'Funis SEO + Ads, free trials, landing pages para palavras de intenção.' },
    { Icon: Target,     title: 'Imobiliárias',        desc: 'SEO local intenso + ads geográficos para áreas e tipologias específicas.' },
    { Icon: LineChart,  title: 'Educação & cursos',   desc: 'Conteúdo informativo, captação de leads, Performance Max para inscrições.' },
  ] : [
    { Icon: MapPin,     title: 'Local businesses',    desc: 'Cafés, restaurants, clinics, shops — dominate "near me" on Google.' },
    { Icon: TrendingUp, title: 'E-commerce',          desc: 'Optimised product pages, Google Shopping, Meta Ads retargeting.' },
    { Icon: Search,     title: 'B2B services',        desc: 'Long-tail keywords, technical content, LinkedIn Ads for decision-makers.' },
    { Icon: BarChart3,  title: 'SaaS / Software',     desc: 'SEO + Ads funnels, free trials, intent-keyword landing pages.' },
    { Icon: Target,     title: 'Real estate',         desc: 'Heavy local SEO + geo ads for specific areas and property types.' },
    { Icon: LineChart,  title: 'Education & courses', desc: 'Informational content, lead capture, Performance Max for enrolments.' },
  ];

  const faqs: FAQItem[] = isPT ? [
    {
      question: 'Em quanto tempo vejo resultados de SEO?',
      answer: 'Os primeiros movimentos em palavras long-tail aparecem em 6-8 semanas. Resultados consistentes em palavras competitivas demoram 4-6 meses. SEO é um investimento de médio prazo — quem promete primeira página em 30 dias mente. Em compensação, os resultados duram anos depois de bem trabalhados.',
    },
    {
      question: 'Qual o budget mínimo para Google Ads ou Meta Ads?',
      answer: 'Recomendamos pelo menos €300-500/mês de gasto em ads para ter dados suficientes para otimizar. Abaixo disso é difícil obter insights estatisticamente úteis. O nosso fee é separado do orçamento de ads — você controla quanto investe na plataforma.',
    },
    {
      question: 'Garantem 1º lugar no Google?',
      answer: 'Não. Ninguém pode garantir isso de forma honesta — depende dos concorrentes, das atualizações da Google e da intenção da pesquisa. O que garantimos é trabalho técnico correto, melhorias mensuráveis nas métricas (impressões, cliques, posições médias) e relatórios transparentes. Sem caixa preta.',
    },
    {
      question: 'O que é "SEO técnico"?',
      answer: 'Trabalho de bastidores que o Google valoriza: velocidade da página (Core Web Vitals), schema.org markup, sitemap correto, URLs amigáveis, hreflang, canonicals, structured data, otimização mobile. Faz a diferença entre um site lento "ignorado" e um site rápido "favorecido".',
    },
    {
      question: 'Trabalham com Google Business Profile?',
      answer: 'Sim — é a base do SEO local. Otimização completa do perfil, gestão de reviews, fotos, publicações, perguntas frequentes, atributos. Para negócios físicos isto traz mais tráfego que qualquer outra alavanca.',
    },
    {
      question: 'Como vou ver os resultados?',
      answer: 'Dashboard Looker Studio com tráfego, posições médias, cliques, conversões, gasto em ads, ROI estimado. Atualizado diariamente. Mais relatório mensal escrito a explicar o que mudou e o que vamos fazer no próximo mês.',
    },
    {
      question: 'Há fidelização?',
      answer: 'Recomendamos pelo menos 3 meses para SEO (precisamos do tempo para mostrar resultados). Após isso é mensal com 30 dias de aviso. Para Ads, podemos começar em qualquer mês — os anúncios funcionam a partir do dia 1.',
    },
  ] : [
    {
      question: 'How long until I see SEO results?',
      answer: 'First movements on long-tail keywords appear in 6-8 weeks. Consistent results on competitive keywords take 4-6 months. SEO is a medium-term investment — anyone promising page one in 30 days is lying. In return, well-built results last for years.',
    },
    {
      question: 'What is the minimum ads budget?',
      answer: 'We recommend at least €300-500/month of ad spend for enough data to optimise. Below that it\'s hard to get statistically meaningful insights. Our fee is separate from ad budget — you control platform spend.',
    },
    {
      question: 'Do you guarantee #1 on Google?',
      answer: 'No. Nobody can honestly — it depends on competitors, Google updates and search intent. What we guarantee: correct technical work, measurable improvements (impressions, clicks, average positions) and transparent reports. No black box.',
    },
    {
      question: 'What is "technical SEO"?',
      answer: 'Behind-the-scenes work Google rewards: page speed (Core Web Vitals), schema.org markup, correct sitemap, friendly URLs, hreflang, canonicals, structured data, mobile optimisation. Difference between a slow "ignored" site and a fast "favoured" one.',
    },
    {
      question: 'Do you work with Google Business Profile?',
      answer: 'Yes — foundation of local SEO. Full profile optimisation, reviews management, photos, posts, FAQs, attributes. For physical businesses this drives more traffic than any other lever.',
    },
    {
      question: 'How will I see results?',
      answer: 'A Looker Studio dashboard with traffic, average positions, clicks, conversions, ad spend, estimated ROI. Updated daily. Plus a monthly written report explaining what changed and what\'s next.',
    },
    {
      question: 'Is there a lock-in?',
      answer: 'We recommend at least 3 months for SEO (we need time to show results). After that, monthly with 30 days notice. For Ads, we can start any month — ads work from day one.',
    },
  ];

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'SEO and Digital Advertising',
    name: isPT ? 'SEO e Publicidade Digital' : 'SEO and Digital Advertising',
    description,
    provider: { '@type': 'Organization', name: 'Spawncore', url: 'https://spawncore.com' },
    areaServed: 'PT',
    audience: { '@type': 'BusinessAudience', audienceType: 'Small Business' },
    url: 'https://spawncore.com/services/seo',
    offers: tiers.map(t => ({
      '@type': 'Offer',
      name: t.name,
      price: t.price.replace(/[^\d]/g, '') || undefined,
      priceCurrency: 'EUR',
      description: t.description,
    })),
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <html lang={isPT ? 'pt' : 'en'} />
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={isPT
          ? 'SEO Portugal, SEO local Azambuja, Google Ads Portugal, Meta Ads, agência SEO, otimização motores de busca, SEO técnico, Google Business Profile, marketing performance, agência marketing digital Portugal'
          : 'SEO Portugal, local SEO, Google Ads Portugal, Meta Ads, SEO agency, search engine optimisation, technical SEO, Google Business Profile, performance marketing, digital marketing agency Portugal'} />
        <link rel="canonical" href="https://spawncore.com/services/seo" />
        <link rel="alternate" hrefLang="pt-PT" href="https://spawncore.com/services/seo" />
        <link rel="alternate" hrefLang="en" href="https://spawncore.com/services/seo" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://spawncore.com/services/seo" />
        <meta property="og:image" content="https://spawncore.com/lovable-uploads/c121560e-1be0-4632-8106-cdadaf79cf04.png" />
        <meta property="og:locale" content={isPT ? 'pt_PT' : 'en_US'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>

      <SafeJsonLd data={serviceSchema} />

      <header>
        <Navigation />
      </header>

      <ServiceBreadcrumb label={t('nav.seo')} url="https://spawncore.com/services/seo" />

      <main>
        {/* Hero */}
        <section className="relative pt-12 pb-20 md:pt-20 md:pb-28 overflow-hidden">
          <div className="pointer-events-none absolute inset-0 aurora-bg opacity-60" aria-hidden="true" />
          <div className="pointer-events-none absolute inset-0 dot-grid opacity-[0.3]" aria-hidden="true" />
          <div className="orb orb-violet top-[14%] right-[10%] h-72 w-72" aria-hidden="true" />
          <div className="orb orb-cyan   bottom-[20%] left-[8%] h-60 w-60" aria-hidden="true" />

          <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <span className="pill-tag mb-8">
              <LineChart className="h-3 w-3" />
              {isPT ? 'SEO & Ads · Foco em retorno' : 'SEO & Ads · ROI-focused'}
            </span>
            <h1 className="font-display text-foreground text-[clamp(2.5rem,7vw,5.5rem)] leading-[0.96] tracking-tight">
              {isPT ? 'Apareça quando ' : 'Show up when '}
              <span className="italic font-display gradient-text-shimmer">
                {isPT ? 'realmente conta' : 'it really counts'}
              </span>
              .
            </h1>
            <p className="mt-7 text-lg md:text-2xl text-muted-foreground max-w-2xl leading-relaxed">
              {t('servicesSeo.subtitle')}
            </p>
            <p className="mt-5 text-base text-muted-foreground/80 max-w-2xl leading-relaxed">
              {isPT
                ? 'SEO técnico, SEO local, conteúdo otimizado, Google Ads e Meta Ads — geridos para pequenas empresas que precisam de retorno mensurável. Sem caixa preta, sem promessas vazias.'
                : 'Technical SEO, local SEO, optimised content, Google Ads and Meta Ads — managed for small businesses that need measurable return. No black box, no empty promises.'}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/contact" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  className="group h-14 px-7 rounded-full bg-primary text-primary-foreground hover:bg-primary-light shadow-glow border border-primary-light/30 text-base w-full sm:w-auto"
                >
                  <LineChart className="mr-2 h-4 w-4" />
                  {t('servicesSeo.cta')}
                  <ArrowUpRight className="ml-2 h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </Button>
              </Link>
              <a href="#pricing" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  variant="outline"
                  className="h-14 px-7 rounded-full bg-card/40 border-border hover:bg-card text-base w-full sm:w-auto"
                >
                  {isPT ? 'Ver preços' : 'See pricing'}
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20 md:py-28 bg-secondary/40 border-y border-border/60">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-14 max-w-3xl">
              <span className="eyebrow">— {t('servicesSeo.increase')}</span>
              <h2 className="mt-4 font-display text-foreground text-[clamp(1.75rem,3.6vw,3rem)] leading-[1.05] tracking-tight">
                {isPT ? 'Três alavancas que mexem o ponteiro.' : 'Three levers that move the needle.'}
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {features.map(({ Icon, key }, idx) => (
                <article
                  key={key}
                  className="group relative overflow-hidden rounded-2xl border border-border/80 bg-card/70 backdrop-blur-sm p-8 transition-all duration-500 hover:bg-card hover:border-primary/40 hover:-translate-y-1"
                >
                  <div className="pointer-events-none absolute -top-20 -right-20 h-48 w-48 rounded-full bg-primary/[0.10] blur-3xl opacity-0 transition-opacity duration-500 group-hover:opacity-100" aria-hidden="true" />
                  <div className="relative">
                    <div className="flex items-start justify-between mb-8">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-border bg-secondary text-primary">
                        <Icon className="h-5 w-5" strokeWidth={1.6} />
                      </div>
                      <span className="font-mono-tag text-[10px] uppercase tracking-[0.32em] text-muted-foreground/70">
                        / 0{idx + 1}
                      </span>
                    </div>
                    <h3 className="font-display text-2xl md:text-[1.625rem] leading-tight tracking-tight mb-3 text-foreground">
                      {t(`servicesSeo.features.${key}.title`)}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {t(`servicesSeo.features.${key}.desc`)}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Use cases */}
        <section className="py-20 md:py-28 bg-background">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-12 gap-6 mb-14">
              <div className="col-span-12 md:col-span-7">
                <span className="eyebrow">— {isPT ? 'Por setor' : 'By sector'}</span>
                <h2 className="mt-4 font-display text-foreground text-[clamp(1.75rem,3.6vw,3rem)] leading-[1.05] tracking-tight">
                  {isPT ? 'Onde SEO + Ads tem mais retorno.' : 'Where SEO + Ads delivers most.'}
                </h2>
              </div>
              <p className="col-span-12 md:col-span-4 md:col-start-9 self-end text-muted-foreground text-base md:text-lg leading-relaxed">
                {isPT
                  ? 'Cada nicho tem palavras-chave próprias. Encontramos as suas.'
                  : 'Every niche has its own keywords. We find yours.'}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {useCases.map((uc, idx) => (
                <article
                  key={uc.title}
                  className="group rounded-xl border border-border/80 bg-card/60 p-6 hover:bg-card hover:border-primary/30 transition-colors"
                >
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-secondary text-primary">
                      <uc.Icon className="h-4 w-4" strokeWidth={1.6} />
                    </div>
                    <div className="font-mono-tag text-[10px] uppercase tracking-[0.32em] text-muted-foreground/70">
                      / 0{idx + 1}
                    </div>
                  </div>
                  <h3 className="font-display text-xl text-foreground mb-2.5 leading-tight">{uc.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{uc.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits / Included */}
        <section className="py-20 md:py-28 bg-secondary/40 border-y border-border/60">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <div className="mb-14 max-w-3xl">
              <span className="eyebrow">— {t('servicesSeo.offer')}</span>
              <h2 className="mt-4 font-display text-foreground text-[clamp(1.75rem,3.6vw,3rem)] leading-[1.05] tracking-tight">
                {isPT ? 'Tudo o que está sempre incluído.' : 'Everything that\'s always included.'}
              </h2>
            </div>
            <ul className="grid md:grid-cols-2 gap-x-12 gap-y-5">
              {benefits.map((benefit, idx) => (
                <li key={idx} className="flex items-start gap-4 group">
                  <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-primary/30 bg-primary/[0.10] text-primary group-hover:bg-primary/[0.20] transition-colors">
                    <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
                  </div>
                  <span className="text-foreground/90 text-base md:text-lg leading-relaxed pt-0.5">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Tools */}
        <section className="py-20 md:py-24 bg-background">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-12 max-w-3xl">
              <span className="eyebrow">— {isPT ? 'Ferramentas' : 'Tools'}</span>
              <h2 className="mt-4 font-display text-foreground text-[clamp(1.75rem,3.6vw,3rem)] leading-[1.05] tracking-tight">
                {isPT ? 'Com o que trabalhamos diariamente.' : 'What we use every day.'}
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border/60 rounded-2xl overflow-hidden border border-border/60">
              {tools.map(({ name, desc }) => (
                <div key={name} className="bg-card/60 p-5 hover:bg-card transition-colors">
                  <div className="h-1 w-6 bg-primary mb-3 rounded-full" />
                  <div className="font-display text-sm text-foreground leading-tight">{name}</div>
                  <div className="text-xs text-muted-foreground mt-1">{desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <div id="pricing">
          <ServicePricing
            eyebrow={isPT ? 'Investimento' : 'Investment'}
            heading={isPT ? 'Pacotes mensais. Sem caixa preta.' : 'Monthly packages. No black box.'}
            subtitle={isPT
              ? 'Ads é faturado separadamente do orçamento de plataforma. Reports e dashboards sempre incluídos.'
              : 'Ads management is invoiced separately from platform spend. Reports and dashboards always included.'}
            tiers={tiers}
            note={isPT
              ? 'Contas Google Ads, Meta Ads e Search Console ficam sempre em seu nome. Pode levar tudo consigo.'
              : 'Google Ads, Meta Ads and Search Console accounts are always in your name. Take everything with you.'}
          />
        </div>

        {/* FAQ */}
        <ServiceFAQ
          eyebrow={isPT ? 'Perguntas frequentes · SEO & Ads' : 'FAQ · SEO & Ads'}
          heading={isPT ? 'Antes de investir um euro.' : 'Before you spend a euro.'}
          subtitle={isPT ? 'Respostas honestas sobre marketing digital.' : 'Honest answers about digital marketing.'}
          faqs={faqs}
        />

        {/* Final CTA */}
        <section className="py-20 md:py-28 bg-background relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0 aurora-bg opacity-50" aria-hidden="true" />
          <div className="pointer-events-none absolute inset-0 dot-grid opacity-[0.3]" aria-hidden="true" />

          <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
            <span className="pill-tag mb-6">
              <LineChart className="h-3 w-3" />
              {isPT ? 'Auditoria gratuita · 30 min' : 'Free 30-min audit'}
            </span>
            <h2 className="font-display text-foreground text-[clamp(2rem,5vw,4rem)] leading-[1.02] tracking-tight mb-6">
              {t('servicesSeo.ready')}
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
              {t('servicesSeo.readyText')}
            </p>
            <Link to="/contact" className="w-full sm:w-auto">
              <Button
                size="lg"
                className="group h-14 px-8 rounded-full bg-primary text-primary-foreground hover:bg-primary-light shadow-glow border border-primary-light/30 text-base w-full sm:w-auto"
              >
                <LineChart className="mr-2 h-4 w-4" />
                {t('servicesSeo.contact')}
                <ArrowUpRight className="ml-2 h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Button>
            </Link>
          </div>
        </section>

        <ServiceCrossSell current="seo" />
      </main>

      <Footer />
    </div>
  );
};

export default ServicesSeo;
